import type { Graph } from '../core/graph';
import { NODE, nodeHeight } from '../core/layout-metrics';

export interface LayoutMove {
  id: string;
  x: number;
  y: number;
}

/**
 * Layered left-to-right layout.
 *
 * Columns come from longest-path depth so a node always sits to the right of
 * everything it consumes — the single property that makes a texture graph
 * readable. Within a column, nodes are ordered by the average row of their
 * inputs, which is a cheap approximation of crossing minimisation and in
 * practice untangles all but the most interconnected graphs.
 *
 * This is what an agent should call after building a graph programmatically:
 * placing nodes by hand from a script reliably produces a mess.
 */
export function computeLayout(graph: Graph): LayoutMove[] {
  const order = graph.topoOrder();
  if (!order.length) return [];

  const depth = new Map<string, number>();
  for (const node of order) {
    let d = 0;
    for (const edge of graph.incoming(node.id)) {
      d = Math.max(d, (depth.get(edge.from) ?? 0) + 1);
    }
    depth.set(node.id, d);
  }

  const columns = new Map<number, string[]>();
  for (const node of order) {
    const d = depth.get(node.id) ?? 0;
    columns.set(d, [...(columns.get(d) ?? []), node.id]);
  }

  const rowOf = new Map<string, number>();
  const moves: LayoutMove[] = [];
  const sortedDepths = [...columns.keys()].sort((a, b) => a - b);

  for (const d of sortedDepths) {
    const ids = columns.get(d)!;
    // Order by the mean row of upstream nodes; roots keep their relative order.
    const keyed = ids.map((id, index) => {
      const parents = graph.incoming(id).map((e) => rowOf.get(e.from));
      const known = parents.filter((p): p is number => p !== undefined);
      const key = known.length ? known.reduce((a, b) => a + b, 0) / known.length : index;
      return { id, key, index };
    });
    keyed.sort((a, b) => a.key - b.key || a.index - b.index);

    let y = 0;
    keyed.forEach((entry, row) => {
      rowOf.set(entry.id, row);
      const node = graph.getNode(entry.id);
      if (!node) return;
      moves.push({ id: entry.id, x: d * (NODE.width + NODE.columnGap), y });
      y += nodeHeight(node.collapsed) + NODE.rowGap;
    });
  }

  // Centre each column vertically so the graph reads as a balanced flow.
  const columnHeights = new Map<number, number>();
  for (const move of moves) {
    const column = Math.round(move.x / (NODE.width + NODE.columnGap));
    const node = graph.getNode(move.id);
    columnHeights.set(
      column,
      Math.max(columnHeights.get(column) ?? 0, move.y + nodeHeight(node?.collapsed)),
    );
  }
  const tallest = Math.max(...columnHeights.values(), 0);
  for (const move of moves) {
    const column = Math.round(move.x / (NODE.width + NODE.columnGap));
    move.y += (tallest - (columnHeights.get(column) ?? 0)) / 2;
  }

  return moves;
}

/**
 * A free position for a new node near `origin`.
 *
 * Nodes added through the API default to (0,0), so a script that adds a dozen
 * of them stacks every one on the same spot and the graph looks empty except
 * for the last. Spiralling outward until nothing overlaps costs nothing and
 * means a programmatically-built graph is legible even before auto-layout runs.
 */
export function findFreePosition(
  graph: Graph,
  origin: { x: number; y: number } = { x: 0, y: 0 },
): { x: number; y: number } {
  const stepX = NODE.width + 24;
  const stepY = nodeHeight(false) + 24;
  const taken = graph.listNodes();

  const overlaps = (x: number, y: number) =>
    taken.some(
      (n) => Math.abs(n.x - x) < NODE.width && Math.abs(n.y - y) < nodeHeight(n.collapsed),
    );

  if (!overlaps(origin.x, origin.y)) return origin;

  // Square rings outward from the origin.
  for (let ring = 1; ring < 32; ring++) {
    for (let dx = -ring; dx <= ring; dx++) {
      for (let dy = -ring; dy <= ring; dy++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== ring) continue;
        const x = origin.x + dx * stepX;
        const y = origin.y + dy * stepY;
        if (!overlaps(x, y)) return { x, y };
      }
    }
  }
  return origin;
}
